import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthRedirectToLogin } from "./nutrimart/hooks/useAuthRedirectToLogin";
import { useProducts } from "@/hooks/useProducts";

import { Ctx } from "./nutrimart/context";
import { T } from "./nutrimart/translations";
import { AI_REC_IDS } from "./nutrimart/data";
import { L, fp, DOP, SHIPPING_RATES } from "./nutrimart/helpers";
import { CSS } from "./nutrimart/styles";
import { CartDrawer } from "./nutrimart/CartDrawer";
import { AppSidebar } from "./nutrimart/AppSidebar";
import { TopBar } from "./nutrimart/TopBar";
import { BottomNav } from "./nutrimart/BottomNav";
import { DeferredFooter } from "./nutrimart/DeferredFooter";
import { OfflineBanner } from "./nutrimart/OfflineBanner";
import { WhatsAppFab } from "./nutrimart/shared/WhatsAppFab";
import { NutriMartRoutes, prefetchDashboard, prefetchProducts } from "./nutrimart/NutriMartRoutes";
import { NutriMartOverlays } from "./nutrimart/NutriMartOverlays";
import { PrefetchStatusIndicator } from "./nutrimart/PrefetchStatusIndicator";
import { CompareStickyBar } from "./nutrimart/shared/CompareStickyBar";

import { UIProvider, useUI } from "./nutrimart/context/UIContext";
import { CartProvider, useCartCtx } from "./nutrimart/context/CartContext";
import { AdminProvider, useAdmin } from "./nutrimart/context/AdminContext";
import { NavProvider, useNav } from "./nutrimart/context/NavContext";

import { useUserRole } from "./nutrimart/hooks/useUserRole";
import { useReferrals } from "./nutrimart/hooks/useReferrals";
import { useOnboarding } from "./nutrimart/hooks/useOnboarding";
import { useFavorites } from "./nutrimart/hooks/useFavorites";
import { useOrders } from "./nutrimart/hooks/useOrders";
import { useCheckout } from "./nutrimart/hooks/useCheckout";
import { useCheckoutFlow } from "./nutrimart/hooks/useCheckoutFlow";
import { useNavState } from "./nutrimart/hooks/useNavState";
import { useLangSync } from "./nutrimart/hooks/useLangSync";
import { useRewards } from "./nutrimart/hooks/useRewards";
import { useWearables } from "./nutrimart/hooks/useWearables";
import { useFilters } from "./nutrimart/hooks/useFilters";
import { useAIChat } from "./nutrimart/hooks/useAIChat";
import { useBodyScan } from "./nutrimart/hooks/useBodyScan";
import { useCommunity } from "./nutrimart/hooks/useCommunity";
import { useAgentAlerts } from "./nutrimart/hooks/useAgentAlerts";
import { useNotifications } from "./nutrimart/hooks/useNotifications";
import { BankTransferInstructions } from "./nutrimart/payments/BankTransferInstructions";
import { useProfile } from "./nutrimart/hooks/useProfile";
import { usePaymentSettings } from "@/lib/payments/usePaymentSettings";
import { useCoachCode } from "./nutrimart/hooks/useCoachCode";

import type { Lang, Product, ShipOpt } from "./nutrimart/types";

import { LEARN_CONTENT } from "./nutrimart/learnContent";

/**
 * Outer component: instantiates providers and passes them shared dependencies
 * (auth user, products, lang, role, toast). The inner Shell consumes them.
 */
export default function NutriMart() {
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === "undefined") return "es";
    // URL ?lang= wins (shareable links), then localStorage, default 'es'.
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang === "en" || urlLang === "es") return urlLang as Lang;
    } catch { /* ignore */ }
    const v = window.localStorage.getItem("nm_lang");
    return v === "en" || v === "es" ? (v as Lang) : "es";
  });
  const { user, loading: authLoading, signOut } = useAuth();
  const { products: P, loading: productsLoading } = useProducts();
  const userRole = useUserRole(user?.id);

  // Persist lang + sync SEO meta tags + push/pull profile preferred_language.
  useLangSync(lang, user?.id, setLang);

  if (authLoading || productsLoading) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
          <div style={{ fontSize: 18, color: "var(--tx2)" }}>Loading...</div>
        </div>
      </>
    );
  }

  return (
    <UIProvider>
      <UIBridge>
        {(toast) => (
          <CartProvider userId={user?.id || null} products={P as Product[]}>
            <AdminProvider userId={user?.id} userRole={userRole} lang={lang} toast={toast}>
              <NavProvider>
                <NutriMartShell
                  lang={lang}
                  setLang={setLang}
                  P={P as Product[]}
                  user={user}
                  userRole={userRole}
                  signOut={signOut}
                />
              </NavProvider>
            </AdminProvider>
          </CartProvider>
        )}
      </UIBridge>
    </UIProvider>
  );
}

/**
 * Tiny helper: exposes the UI `toast` function to outer providers (Admin needs
 * it) without forcing them to live inside the shell tree.
 */
function UIBridge({ children }: { children: (toast: (m: string) => void) => React.ReactNode }) {
  const { toast } = useUI();
  return <>{children(toast)}</>;
}

interface ShellProps {
  lang: Lang;
  setLang: React.Dispatch<React.SetStateAction<Lang>>;
  P: Product[];
  user: any;
  userRole: string;
  signOut: () => Promise<void> | void;
}

function NutriMartShell({ lang, setLang, P, user, userRole, signOut }: ShellProps) {
  const t = T[lang];
  const authed = !!user;

  const ui = useUI();
  const { toast, modal, setModal, sideOpen, setSideOpen, cartOpen, setCartOpen, darkMode, setDarkMode, quickView, setQuickView, pageLoading, setPageLoading, toasts } = ui;
  const cartCtx = useCartCtx();
  const { cart, addToCart: hookAddToCart, updateQty, removeFromCart, clearCart, cartCnt, cartTot } = cartCtx;
  const adminCtx = useAdmin();
  const { adminOrders, adminTab, setAdminTab, adminTrackingInputs, setAdminTrackingInputs, adminAdvanceStatus, adminSetTracking, adminCreateShipment, mapStatus } = adminCtx;

  const { profile: userProfile, setProfile: setUserProfile, save: saveProfile, saving: profileSaving, loading: profileLoading } = useProfile(user?.id, user?.email);

  const { page, setPage, selProd, setSelProd, installNav, pendingDetailId, setPendingDetailId } = useNav();

  // Gate only protected pages (account, checkout, admin, …); the
  // landing/home, product browsing and learn pages stay public so
  // visitors can explore the demo without signing up.
  useAuthRedirectToLogin({ user, loading: false, page });

  const { favs, toggle: togFav } = useFavorites();

  const { search, setSearch, catF, setCatF, goalF, sortBy, setSortBy, searchOpen, setSearchOpen, recentSearches, setRecentSearches } = useFilters();

  const [aiGoal, setAiGoal] = useState<string>("muscle");
  const [qty, setQty] = useState<number>(1);
  const { ckStep, setCkStep, shipId, setShipId, orderDone, setOrderDone, promoCode, setPromoCode, appliedPromo, setAppliedPromo, payMethod, setPayMethod, azulPending, setAzulPending } = useCheckout();

  // E2E helper: allow tests to force an invalid shipId for edge-case coverage.
  useEffect(() => {
    (window as any).__nm_e2e_setShipId = (id: string) => setShipId(id as any);
    return () => { delete (window as any).__nm_e2e_setShipId; };
  }, [setShipId]);
  const [cmpIds, setCmpIds] = useState<Array<Product["id"]>>([]);

  const { rewardPts, setRewardPts, autoships, setAutoships } = useRewards(1250);
  const [whatsappOn, setWhatsappOn] = useState<boolean>(true);
  const [trackerLog, setTrackerLog] = useState<Record<string, any>>({});
  const [streak, setStreak] = useState<number>(7);

  useAIChat(lang); // mount only — chat state lives inside hook for now
  const [stackGoal, setStackGoal] = useState<string | null>(null);
  const [scanInput, setScanInput] = useState<string>("");
  const [scanResult, setScanResult] = useState<any>(null);
  const [newReviewText, setNewReviewText] = useState<string>("");
  const [newReviewRating, setNewReviewRating] = useState<number>(5);
  const [userReviews, setUserReviews] = useState<any[]>([]);
  const [learnTab, setLearnTab] = useState<string>("all");

  const { code: refCode, count: refCount, earned: refEarned, tier: refTier, list: refList } = useReferrals(user?.id);
  const { show: showOnboarding, finish: finishOnboarding } = useOnboarding(user?.id);
  const { wearDevices, setWearDevices, bioData } = useWearables();
  const { commStacks, commTab, setCommTab, commCommentInput, setCommCommentInput, myStackName, setMyStackName, showCreateStack, setShowCreateStack } = useCommunity();
  const { agentAlerts, setAgentAlerts, agentTab, setAgentTab } = useAgentAlerts();
  const { bodyScanData, selectedScan, setSelectedScan, bsTab, setBsTab, compA, setCompA, compB, setCompB, bodyGoals, setBodyGoals, editingGoals, setEditingGoals, goalDraft, setGoalDraft } = useBodyScan();

  const [obStep, setObStep] = useState<number>(0);
  const [obGoal, setObGoal] = useState<string | null>(null);
  const [obDiets, setObDiets] = useState<string[]>([]);
  const [obWearable, setObWearable] = useState<string | null>(null);

  const trendingSearches = lang === "es" ? ["proteína whey", "vitamina D", "creatina", "matcha", "ashwagandha"] : ["whey protein", "vitamin D", "creatine", "matcha", "ashwagandha"];
  const ingredientSearch = lang === "es" ? ["Ashwagandha", "Creatina", "Colágeno", "Espirulina", "Magnesio", "Omega-3"] : ["Ashwagandha", "Creatine", "Collagen", "Spirulina", "Magnesium", "Omega-3"];

  const { notifs, setNotifs, notifTab, setNotifTab, showNotifs, setShowNotifs, deleteNotif } = useNotifications(P as any);

  const healthData = { routineScore: 78, consistency: 85, daysTracked: 24, monthlySpend: 189.96, sleepAvg: 74, streak2: streak, topProducts: [1, 9, 7, 3], monthlyTrend: [62, 68, 71, 74, 78], insights: [{ metric: { en: "Sleep Quality", es: "Calidad de Sueño" }, status: "improving", detail: { en: "Up 12% since adding Magnesium Glycinate to your evening routine.", es: "Subió 12% desde agregar Glicinato de Magnesio a tu rutina nocturna." } }, { metric: { en: "Recovery Speed", es: "Velocidad de Recuperación" }, status: "stable", detail: { en: "Collagen + BCAAs keeping your recovery consistent at 82%.", es: "Colágeno + BCAAs mantienen tu recuperación consistente al 82%." } }, { metric: { en: "Energy Levels", es: "Niveles de Energía" }, status: "attention", detail: { en: "Dropped 8% this week. Consider adding Matcha or B-vitamins.", es: "Bajó 8% esta semana. Considera agregar Matcha o vitaminas B." } }] };

  const { orders, setOrders, selOrder, setSelOrder } = useOrders(user?.id, lang, toast);

  const addCart = useCallback((p: Product, q = 1) => { hookAddToCart(p as any, q); toast(`✅ ${t.addedToCart}`); }, [hookAddToCart, toast, t]);

  // Trainer / coach code (B2B2C). Discount stacks AFTER promo, on cart subtotal.
  // When an order is created with `validatedCoach`, we record a row in
  // `coach_attributions` so the trainer's portal shows the sale + commission.
  const {
    coachCodeInput, setCoachCodeInput, validatedCoach,
    applyCoachCode, clearCoach, coachDisc, validating: coachValidating, coachError,
  } = useCoachCode(cartTot);

  // COD surcharge: settings store DOP, totals run in USD.
  const { settings: paymentSettings } = usePaymentSettings();
  const codSurchargeDOP = payMethod === "cod" ? (paymentSettings?.cod_surcharge ?? 0) : 0;
  const codSurchargeUSD = codSurchargeDOP / DOP;

  // ─── Shipping methods ──────────────────────────────────────────────
  // 1) Standard — Gran Santo Domingo (predetermined flat fee by the store)
  // 2) Express  — in-area (NutriMart messenger). Free if order ≥ threshold (store-defined eligibility)
  // 3) National — CaribePack (predetermined flat fee by the courier)
  const expressFreeThresholdUSD =
    paymentSettings?.express_free_threshold_usd ?? SHIPPING_RATES.express_free_min_usd;
  const expressEligibleForFree = cartTot >= expressFreeThresholdUSD;
  const shipOpts: ShipOpt[] = [
    {
      id: "standard",
      nm: lang === "es" ? "Envío estándar — Gran Santo Domingo" : "Standard shipping — Greater Santo Domingo",
      tm: lang === "es" ? "1-2 días laborables · tarifa fija" : "1-2 business days · flat rate",
      pr: SHIPPING_RATES.standard_gsd_usd,
    },
    {
      id: "express",
      nm: lang === "es" ? "Express en área (mensajero NutriMart)" : "In-area express (NutriMart messenger)",
      tm: expressEligibleForFree
        ? (lang === "es" ? "Mismo día · gratis (pedido elegible)" : "Same day · free (eligible order)")
        : (lang === "es" ? `Mismo día · gratis desde ${fp(expressFreeThresholdUSD, lang)}` : `Same day · free over ${fp(expressFreeThresholdUSD, lang)}`),
      pr: expressEligibleForFree ? 0 : SHIPPING_RATES.express_local_usd,
    },
    {
      id: "national",
      nm: lang === "es" ? "Envío nacional — CaribePack" : "National shipping — CaribePack",
      tm: lang === "es" ? "2-5 días laborables · tarifa CaribePack" : "2-5 business days · CaribePack rate",
      pr: SHIPPING_RATES.national_caribepack_usd,
    },
    {
      id: "pickup",
      nm: lang === "es" ? "Recogida en tienda" : "Store pickup",
      tm: lang === "es" ? "Gratis · listo en 2-4 horas" : "Free · ready in 2-4 hours",
      pr: 0,
    },
  ];
  const shipCost = shipOpts.find((o) => o.id === shipId)?.pr ?? 0;

  const filtered = useMemo(() => {
    let p2: Product[] = [...P];
    if (catF !== "all") p2 = p2.filter((x: any) => x.cat === catF);
    if (goalF) p2 = p2.filter((x: any) => x.goals?.includes(goalF));
    if (search) {
      const q = search.toLowerCase();
      p2 = p2.filter((x: any) => L(x.nm, lang).toLowerCase().includes(q) || (x.br || "").toLowerCase().includes(q));
    }
    if (sortBy === "price_low") p2.sort((a, b) => a.pr - b.pr);
    if (sortBy === "price_high") p2.sort((a, b) => b.pr - a.pr);
    if (sortBy === "rating") p2.sort((a, b) => b.rt - a.rt);
    if (sortBy === "featured") p2.sort((a: any, b: any) => (b.feat ? 1 : 0) - (a.feat ? 1 : 0));
    return p2;
  }, [P, catF, goalF, search, sortBy, lang]);

  const aiProds = useMemo(
    () => ((AI_REC_IDS as any)[aiGoal] || []).map((id: any) => P.find((p: any) => p.id === id)).filter(Boolean),
    [P, aiGoal],
  );
  const $ = (usd: number) => fp(usd, lang);

  // Enriched `nav` + deep-link resolver. Registers itself into NavContext.
  const { nav } = useNavState({
    page, setPage, selProd, setSelProd, installNav,
    pendingDetailId, setPendingDetailId,
    setSideOpen, setPageLoading,
    setOrderDone, setCkStep, setQty, P,
  });

  const completeOrder = useCallback((oid: string) => {
    setModal({
      title: t.completeOrderQ,
      text: `${oid}?`,
      onConfirm: () => {
        setOrders((p) => p.map((o) => (o.id === oid ? { ...o, status: "Finished", ca: new Date().toISOString().split("T")[0] } : o)));
        setRewardPts((p) => p + 50);
        toast(`✅ ${oid} → ${t.Finished} (+50 pts)`);
        setModal(null);
      },
    });
  }, [toast, t, setOrders, setRewardPts, setModal]);

  // Full order placement flow + express checkout + promo application.
  // Encapsulates ~230 lines of DB inserts, payment intents, loyalty,
  // coach attribution, order confirmation email and AZUL hand-off.
  const {
    promoDisc, applyPromo,
    doCheckout, doExpressCheckout, expressEligibility,
    pointsToRedeem, setPointsToRedeem,
    transferTx, setTransferTx,
  } = useCheckoutFlow({
    user, lang, t, cart, cartCnt, cartTot, clearCart, toast, setModal,
    ckStep, setCkStep, payMethod, setPayMethod, shipId, setShipId, shipOpts, shipCost,
    appliedPromo, setAppliedPromo, promoCode, setPromoCode,
    setOrderDone, setAzulPending, setCartOpen,
    paymentSettings, rewardPts, setRewardPts, validatedCoach, clearCoach,
    setOrders, nav,
  });

  const ctx = { addCart, togFav, favs, lang, t, $, setCmpIds, cmpIds, toast, setQuickView };

  const routesProps: any = {
    page, P, lang, setLang, t, $, addCart, togFav, favs, pageLoading,
    aiGoal, setAiGoal, aiProds, orders, wearDevices, bioData, bodyScanData,
    agentAlerts, setAgentAlerts, commStacks, setCatF, catF, sortBy, setSortBy,
    filtered, selProd, qty, setQty, setAutoships, autoships, toast,
    newReviewText, setNewReviewText, newReviewRating, setNewReviewRating,
    userReviews, setUserReviews, stackGoal, setStackGoal,
    scanInput, setScanInput, scanResult, setScanResult,
    streak, setStreak, trackerLog, setTrackerLog,
    rewardPts, setRewardPts, refCode, refCount, refEarned, refTier, refList,
    setWearDevices, commTab, setCommTab, commCommentInput, setCommCommentInput,
    myStackName, setMyStackName, showCreateStack, setShowCreateStack, cart,
    agentTab, setAgentTab, healthData, learnContent: LEARN_CONTENT, learnTab, setLearnTab,
    selectedScan, setSelectedScan, bsTab, setBsTab, compA, setCompA, compB, setCompB,
    bodyGoals, setBodyGoals, editingGoals, setEditingGoals, goalDraft, setGoalDraft,
    notifs, setNotifs, notifTab, setNotifTab, deleteNotif,
    ckStep, setCkStep, shipId, setShipId, shipOpts, shipCost, payMethod, setPayMethod,
    whatsappOn, setWhatsappOn, orderDone, setOrderDone, doCheckout, applyPromo, promoCode, setPromoCode,
    appliedPromo, promoDisc, cartTot,
    // Coach code (B2B2C)
    coachCodeInput, setCoachCodeInput, validatedCoach, applyCoachCode, clearCoach, coachDisc,
    coachValidating, coachError,
    pointsToRedeem, setPointsToRedeem,
    azulPending, setAzulPending,
    codSurchargeUSD, codSurchargeDOP,
    selOrder, setSelOrder, completeOrder,
    cmpIds, setCmpIds, user, userRole,
    adminOrders, adminTab, setAdminTab, adminTrackingInputs, setAdminTrackingInputs,
    adminSetTracking, adminCreateShipment, adminAdvanceStatus, mapStatus,
    userProfile, setUserProfile, saveProfile, profileSaving, profileLoading,
    transferTx, setTransferTx,
  };

  return (
    <Ctx.Provider value={ctx as any}>
      <style>{CSS}</style>
      <div className={`app ${darkMode ? "dark-mode" : ""}`}>
        <AppSidebar sideOpen={sideOpen} lang={lang} setLang={setLang as any} t={t} user={user} signOut={signOut} userRole={userRole} rewardPts={rewardPts} cmpIds={cmpIds} agentAlerts={agentAlerts} prefetchDashboard={prefetchDashboard} prefetchProducts={prefetchProducts} />
        <main className="mn">
          <OfflineBanner lang={lang} />
          <TopBar sideOpen={sideOpen} setSideOpen={setSideOpen as any} search={search} setSearch={setSearch} setSearchOpen={setSearchOpen} lang={lang} setLang={setLang as any} darkMode={darkMode} setDarkMode={setDarkMode as any} notifs={notifs} showNotifs={showNotifs} setShowNotifs={setShowNotifs} setNotifs={setNotifs as any} setCartOpen={setCartOpen as any} cartCnt={cartCnt} t={t} prefetchDashboard={prefetchDashboard} P={P as Product[]} setCatF={setCatF as any} rewardPts={rewardPts} authed={authed} />
          <div className="pg">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={page + (selProd ? selProd.id : "")}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{
                  opacity: { duration: 0.18, ease: "easeOut" },
                  y: { duration: 0.22, ease: [0.2, 0.8, 0.2, 1] },
                }}
                style={{ willChange: "opacity, transform" }}
              >
                <NutriMartRoutes {...routesProps} />
              </motion.div>
            </AnimatePresence>
          </div>
          <DeferredFooter lang={lang} />
          <BottomNav t={t} cartCnt={cartCnt} setCartOpen={setCartOpen as any} agentAlerts={agentAlerts} lang={lang} prefetchDashboard={prefetchDashboard} prefetchProducts={prefetchProducts} />
        </main>
        <PrefetchStatusIndicator lang={lang} />
        <WhatsAppFab lang={lang} />
        <CompareStickyBar cmpIds={cmpIds} setCmpIds={setCmpIds as any} page={page} lang={lang} />
        <CartDrawer
          cartOpen={cartOpen}
          setCartOpen={setCartOpen as any}
          cart={cart}
          cartCnt={cartCnt}
          cartTot={cartTot}
          updateQty={updateQty}
          removeFromCart={removeFromCart}
          t={t}
          $={$}
          lang={lang}
          expressEligible={expressEligibility.eligible}
          expressMethod={expressEligibility.payMethod}
          onExpressCheckout={doExpressCheckout}
          products={P}
          addCart={addCart}
        />
        <NutriMartOverlays
          quickView={quickView} setQuickView={setQuickView} lang={lang} t={t} $={$} addCart={addCart}
          modal={modal} setModal={setModal}
          showOnboarding={showOnboarding} authed={authed} obStep={obStep} setObStep={setObStep}
          obGoal={obGoal} setObGoal={setObGoal} obDiets={obDiets} setObDiets={setObDiets}
          obWearable={obWearable} setObWearable={setObWearable} finishOnboarding={finishOnboarding} setAiGoal={setAiGoal}
          searchOpen={searchOpen} setSearchOpen={setSearchOpen} search={search} setSearch={setSearch}
          recentSearches={recentSearches} setRecentSearches={setRecentSearches}
          trendingSearches={trendingSearches} ingredientSearch={ingredientSearch} P={P} toasts={toasts}
        />
      </div>
    </Ctx.Provider>
  );
}
